import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Phone, ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import logo from "@/assets/kinship-logo.webp";
import EmailLink from "@/components/EmailLink";

type NavItem = { to: string; label: string; items?: { to: string; label: string }[] };

/** Single ordered nav so the header reads left-to-right in the order families need it. */
const navItems: NavItem[] = [
  {
    to: "/about",
    label: "About",
    items: [
      { to: "/about", label: "About" },
      { to: "/for-caregivers", label: "Join Our Team" },
    ],
  },
  {
    to: "/services",
    label: "Services",
    items: [
      { to: "/services", label: "All Services" },
      { to: "/senior-care-nashville", label: "Senior Care" },
      { to: "/home-care-nashville", label: "In-Home Care" },
      { to: "/elder-care-nashville", label: "Elder Care" },
      { to: "/respite-care-nashville", label: "Respite Care" },
      { to: "/for-seniors", label: "For Seniors" },
      { to: "/for-families", label: "For Families" },
      { to: "/consultation", label: "Free Consultation" },
    ],
  },
  {
    to: "/resources",
    label: "Resources",
    items: [
      { to: "/resources", label: "All Resources" },
      { to: "/free-resources", label: "Free Tools & Checklists" },
      { to: "/tools/is-it-time-for-help", label: "Is It Time for Help?" },
      { to: "/tools/caregiver-burnout-check", label: "Free Caregiver Burnout Check" },
      { to: "/resources/guides", label: "Guides & Articles" },
      { to: "/guides/long-distance-caregiving", label: "Long-Distance Caregiving Guide" },
      { to: "/guides/caregiver-burnout", label: "Caregiver Burnout Guide" },
      { to: "/resources/local-help", label: "Nashville Local Help" },
    ],
  },
  {
    to: "/shop",
    label: "Care Shop",
    items: [
      { to: "/shop", label: "All Products" },
      { to: "/shop/book", label: "The Survival Guide (Book)" },
      { to: "/shop/caregiver-kit", label: "Caregiver Kits & Plans" },
      { to: "/shop/wellness-planner", label: "Caregiver Wellness Planner" },
      { to: "/shop/products", label: "Products That Make Life Easier" },
    ],
  },
  {
    to: "/areas/nashville",
    label: "Greater Nashville Area",
    items: [
      { to: "/areas/nashville", label: "Nashville" },
      { to: "/areas/brentwood", label: "Brentwood" },
      { to: "/areas/franklin", label: "Franklin" },
      { to: "/areas/belle-meade", label: "Belle Meade" },
      { to: "/areas/bellevue", label: "Bellevue" },
    ],
  },

  { to: "/partners", label: "For Partners" },

  { to: "/contact", label: "Contact" },

];

const isActive = (pathname: string, to: string) => pathname === to;

const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <header
      className={`sticky top-0 z-50 border-b transition-shadow duration-300 ${
        scrolled
          ? "bg-background shadow-md"
          : "bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/80"
      }`}
    >
      {/* Top bar with phone — desktop only */}
      <div className="hidden md:block bg-primary text-primary-foreground">
        <div className="container flex flex-wrap items-center justify-end gap-x-3 gap-y-1 py-1.5 text-xs">
          <a href="tel:+16158070618" className="flex items-center gap-1.5 hover:underline">
            <Phone className="h-3 w-3" />
            Call or Text (615) 807-0618
          </a>
          <span className="opacity-40">|</span>
          <EmailLink className="break-all hover:underline" />
        </div>
      </div>

      <nav className="container flex h-16 items-center justify-between gap-4">
        <Link to="/" className="flex min-w-0 items-center gap-2 sm:gap-2.5 text-foreground">
          <img src={logo} alt="Kinship Care Collective — senior and elder care in Nashville" className="h-10 w-10 shrink-0 sm:h-12 sm:w-12" width={48} height={48} decoding="async" />
          <span className="flex min-w-0 flex-col leading-tight">
            <span className="font-heading text-base font-semibold sm:text-lg md:text-xl">Kinship Care Collective</span>
            <span className="text-[0.6rem] uppercase tracking-[0.12em] text-muted-foreground sm:text-[0.7rem] sm:tracking-[0.18em]">
              Senior &amp; Elder Care · Nashville
            </span>
          </span>
        </Link>

        {/* Desktop */}
        <div className="hidden lg:flex flex-1 items-center justify-end gap-4 lg:gap-6">
          {navItems.map((item) =>
            item.items ? (
              (() => {
                const active =
                  isActive(location.pathname, item.to) ||
                  item.items.some((i) => isActive(location.pathname, i.to));
                return (
                  <DropdownMenu key={item.to}>
                    <DropdownMenuTrigger
                      className={`flex shrink-0 items-center gap-1 whitespace-nowrap text-sm font-medium transition-colors hover:text-primary outline-none ${
                        active ? "text-primary" : "text-muted-foreground"
                      }`}
                    >
                      {item.label}
                      <ChevronDown className="h-3 w-3" />
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="start" className="min-w-[12rem]">
                      {item.items.map((sub) => (
                        <DropdownMenuItem key={sub.to} asChild>
                          <Link
                            to={sub.to}
                            className={`cursor-pointer ${
                              isActive(location.pathname, sub.to) ? "text-primary" : "text-foreground"
                            }`}
                          >
                            {sub.label}
                          </Link>
                        </DropdownMenuItem>
                      ))}
                    </DropdownMenuContent>
                  </DropdownMenu>
                );
              })()
            ) : (
              <Link
                key={item.to}
                to={item.to}
                className={`shrink-0 whitespace-nowrap text-sm font-medium transition-colors hover:text-primary relative ${
                  isActive(location.pathname, item.to)
                    ? "text-primary after:absolute after:bottom-[-4px] after:left-0 after:right-0 after:h-0.5 after:bg-primary after:rounded-full"
                    : "text-muted-foreground"
                }`}
              >
                {item.label}
              </Link>
            ),
          )}
        </div>

        <div className="hidden lg:block shrink-0">
          <Button asChild size="sm" className="whitespace-nowrap">
            <Link to="/contact">Request a Consultation</Link>
          </Button>
        </div>

        {/* Mobile toggle */}
        <button className="lg:hidden p-2" onClick={() => setOpen(!open)} aria-label="Toggle menu">
          {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </nav>

      {/* Mobile menu */}
      <div
        className={`lg:hidden overflow-y-auto overscroll-contain transition-all duration-300 ease-in-out ${
          open ? "max-h-[calc(100dvh-4rem)] opacity-100" : "max-h-0 opacity-0 overflow-hidden"
        }`}
      >
        <div className="border-t bg-background px-4 pb-4">
          {navItems.map((item) => (
            <div key={item.to}>
              <Link
                to={item.to}
                onClick={() => setOpen(false)}
                className={`block py-3 text-sm font-medium transition-colors border-b border-border/50 ${
                  isActive(location.pathname, item.to) ? "text-primary" : "text-muted-foreground"
                }`}
              >
                {item.label}
              </Link>
              {item.items && (
                <div className="pl-4">
                  {item.items
                    .filter((sub) => sub.to !== item.to)
                    .map((sub) => (
                      <Link
                        key={sub.to}
                        to={sub.to}
                        onClick={() => setOpen(false)}
                        className={`block py-2.5 text-sm transition-colors border-b border-border/30 ${
                          isActive(location.pathname, sub.to) ? "text-primary" : "text-muted-foreground"
                        }`}
                      >
                        {sub.label}
                      </Link>
                    ))}
                </div>
              )}
            </div>
          ))}

          <Button asChild size="sm" className="mt-3 w-full">
            <Link to="/contact" onClick={() => setOpen(false)}>
              Request a Consultation
            </Link>
          </Button>
        </div>
      </div>
    </header>
  );
};

export default Navbar;
